import { computed, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import {
  generateWriteModel,
  generateSingleEntity,
  generateReadModelFromEntity,
  generateEntitiesFromWorkflow,
  generateDataFieldExampleData,
  generateReadModelFromWorkflow,
  generateEntitiesFromDescription
} from '@/ai'

const usePrompts = () => {
  const { proxy } = getCurrentInstance()
  const store = useStore()
  const workflow = computed(() => store.state.workflow.workflow)
  const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
  const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
  const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])

  const getEntitiesFromWorkflow = async () => {
    const abortController = new AbortController()

    return await generateEntitiesFromWorkflow({
      workflow: workflow.value,
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      cardTypes: requirementTypes.value,
      abortController
    })
  }

  const getEntitiesFromDescription = async (userDescription) => {
    const abortController = new AbortController()

    return await generateEntitiesFromDescription({
      workflow: workflow.value,
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      cardTypes: requirementTypes.value,
      userDescription,
      abortController
    })
  }

  const getReadModelFromWorkflow = async (eventId) => {
    if (!workflow.value.aiDescription) {
      proxy.$loadingState.stop()
      store.commit('snackbar/showMessage', {
        content: 'Please enter a description in settings: Get help from AI to build your workflow',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      return
    }

    const abortController = new AbortController()

    return await generateReadModelFromWorkflow({
      eventId,
      workflow: workflow.value,
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      cardTypes: requirementTypes.value,
      abortController
    })
  }

  const getReadModelFromEntity = async (eventId, entity) => {
    if (!workflow.value.aiDescription) {
      proxy.$loadingState.stop()
      store.commit('snackbar/showMessage', {
        content: 'Please enter a description in settings: Get help from AI to build your workflow',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      return
    }

    const abortController = new AbortController()

    return await generateReadModelFromEntity({
      eventId,
      workflow: workflow.value,
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      cardTypes: requirementTypes.value,
      entity,
      abortController
    })
  }

  const getWriteModel = async (eventId) => {
    if (!workflow.value.aiDescription) {
      proxy.$loadingState.stop()
      store.commit('snackbar/showMessage', {
        content: 'Please enter a description in settings: Get help from AI to build your workflow',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      return
    }

    const abortController = new AbortController()

    return await generateWriteModel({
      eventId,
      workflow: workflow.value,
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      cardTypes: requirementTypes.value,
      abortController
    })
  }

  const getSingleEntity = async (eventId, name, entityDescription) => {
    const abortController = new AbortController()

    return await generateSingleEntity({
      eventId,
      workflow: workflow.value,
      svgGraph: svgGraph.value,
      svgLanes: svgLanes.value,
      cardTypes: requirementTypes.value,
      name,
      entityDescription,
      abortController
    })
  }

  const getDataFieldExampleData = async (entity, dataField) => {
    const abortController = new AbortController()

    return await generateDataFieldExampleData({
      aiVersion: workflow.value.aiVersion,
      entity,
      dataField,
      abortController
    })
  }

  return {
    getWriteModel,
    getSingleEntity,
    getReadModelFromEntity,
    getEntitiesFromWorkflow,
    getDataFieldExampleData,
    getReadModelFromWorkflow,
    getEntitiesFromDescription
  }
}

export default usePrompts
